import { Editor } from "@tiptap/react";
import { message } from "antd";

import { uploadReleaseNoteImage } from "../../releaseNotesApi";
import {
  dataUrlToFile,
  filenameFromSrc,
  isRehostableImageSrc,
} from "./pasteImageUtils";

const MAX_REMOVE_PASSES = 200;

async function srcToImageFile(src: string): Promise<File> {
  const filename = filenameFromSrc(src);
  if (src.startsWith("data:")) return dataUrlToFile(src, filename);
  const response = await fetch(src);
  if (!response.ok) throw new Error(`Failed to fetch ${src}`);
  const blob = await response.blob();
  if (!blob.type.startsWith("image/")) throw new Error("Not an image");
  return new File([blob], filename, { type: blob.type });
}

export function collectImageSrcs(editor: Editor): Set<string> {
  const srcs = new Set<string>();
  editor.state.doc.descendants((node) => {
    if (node.type.name === "image") {
      const src = node.attrs.src as string | undefined;
      if (src) srcs.add(src);
    }
    return true;
  });
  return srcs;
}

function collectForeignSrcs(editor: Editor, skip: Set<string>): string[] {
  const srcs = new Set<string>();
  editor.state.doc.descendants((node) => {
    if (node.type.name === "image") {
      const src = node.attrs.src as string | undefined;
      if (src && !skip.has(src) && isRehostableImageSrc(src)) srcs.add(src);
    }
    return true;
  });
  return Array.from(srcs);
}

function updateImagesBySrc(
  editor: Editor,
  oldSrc: string,
  newSrc: string
): void {
  const { tr } = editor.state;
  let changed = false;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.src === oldSrc) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: newSrc });
      changed = true;
    }
    return true;
  });
  if (changed) editor.view.dispatch(tr);
}

function removeImagesBySrc(editor: Editor, src: string): void {
  for (let pass = 0; pass < MAX_REMOVE_PASSES; pass += 1) {
    if (editor.isDestroyed) return;
    let from = -1;
    let size = 0;
    editor.state.doc.descendants((node, pos) => {
      if (from >= 0) return false;
      if (node.type.name === "image" && node.attrs.src === src) {
        from = pos;
        size = node.nodeSize;
        return false;
      }
      return true;
    });
    if (from < 0) return;
    editor.view.dispatch(editor.state.tr.delete(from, from + size));
  }
}

export async function rehostForeignImages(
  editor: Editor,
  skip: Set<string>
): Promise<void> {
  const srcs = collectForeignSrcs(editor, skip);
  if (srcs.length === 0) return;
  let failures = 0;
  for (const src of srcs) {
    try {
      const file = await srcToImageFile(src);
      if (editor.isDestroyed) return;
      const url = await uploadReleaseNoteImage(file);
      if (editor.isDestroyed) return;
      updateImagesBySrc(editor, src, url);
    } catch {
      if (editor.isDestroyed) return;
      failures += 1;
      removeImagesBySrc(editor, src);
    }
  }
  if (failures > 0) {
    message.warning(
      "Couldn't import some pasted images — please upload them manually."
    );
  }
}

export async function uploadPastedImageFiles(
  editor: Editor,
  files: File[]
): Promise<void> {
  for (const file of files) {
    try {
      const url = await uploadReleaseNoteImage(file);
      if (editor.isDestroyed) return;
      editor.chain().focus().setImage({ src: url }).run();
    } catch {
      if (editor.isDestroyed) return;
      message.error("Failed to upload the pasted image.");
    }
  }
}
